import { Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatRelativeFr } from "@/components/worker/workerFormatters";
import {
  QueueDetails,
  QueueKey,
  QueueSummaryResponse,
  QueueSummarySection,
  QUEUE_LABELS,
  QUEUE_ORDER,
} from "@/services/workerApi";

function payloadSummary(item: unknown): string {
  try {
    return JSON.stringify(item);
  } catch {
    return String(item);
  }
}

// Détail d'une file : sélection de la file via des boutons-compteurs (le nombre
// actuel par file, façon anciennes cartes cliquables), puis table de détail
// (statut, ancienneté, payload tronqué + tooltip JSON, suppression unitaire).
export function QueueDetailTable({
  details,
  detailsType,
  summary,
  onChangeType,
  onDelete,
  onFlush,
  loading,
}: {
  details: QueueDetails | null;
  detailsType: QueueKey;
  summary: QueueSummaryResponse | null;
  onChangeType: (t: QueueKey) => void;
  onDelete: (item: unknown) => void;
  onFlush: () => void;
  loading?: boolean;
}) {
  const items = details?.items ?? [];

  return (
    <div className="space-y-3">
      {/* Boutons-compteurs : une file par bouton, avec son nombre actuel. */}
      <div className="flex flex-wrap items-center gap-2">
        {QUEUE_ORDER.map((k) => {
          const section = summary?.[k] as QueueSummarySection | undefined;
          const count = section?.total_count ?? section?.count ?? 0;
          const active = k === detailsType;
          return (
            <Button
              key={k}
              type="button"
              variant={active ? "default" : "outline"}
              size="sm"
              className="gap-2"
              onClick={() => onChangeType(k)}
            >
              {QUEUE_LABELS[k]}
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums",
                  active ? "bg-primary-foreground/20" : "bg-muted text-foreground",
                )}
              >
                {count.toLocaleString("fr-FR")}
              </span>
            </Button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {QUEUE_LABELS[detailsType]} —{" "}
          {(details?.pending_count ?? details?.total ?? 0).toLocaleString("fr-FR")} en attente
          {details?.processing_count
            ? ` · ${details.processing_count.toLocaleString("fr-FR")} en cours`
            : ""}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={onFlush}
          disabled={!items.length}
        >
          <Trash2 className="mr-1 h-3.5 w-3.5" />
          Vider la file
        </Button>
      </div>

      <TooltipProvider delayDuration={150}>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">Statut</TableHead>
                <TableHead className="w-[140px]">Ajouté</TableHead>
                <TableHead>Payload</TableHead>
                <TableHead className="w-[90px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                    Aucune tâche en file
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row: { item: unknown; score?: number; status?: string }, idx) => {
                  const payload = payloadSummary(row.item);
                  const rel = formatRelativeFr(new Date((row.score ?? 0) * 1000).toISOString());
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        {row.status === "processing" ? (
                          <Badge variant="secondary">En cours</Badge>
                        ) : (
                          <Badge variant="outline">En attente</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{rel ?? "—"}</TableCell>
                      <TableCell className="max-w-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="block cursor-help truncate font-mono text-xs">
                              {payload}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-md">
                            <pre className="whitespace-pre-wrap break-all text-xs">
                              {JSON.stringify(row.item, null, 2)}
                            </pre>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          title="Supprimer cet élément"
                          onClick={() => onDelete(row.item)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </TooltipProvider>
    </div>
  );
}
